import React, { useState, useCallback } from 'react'
import { Modal, Form, DatePicker, message, Spin } from 'antd'
import moment from 'moment'
import debounce from 'lodash/debounce'
import { _exportBikeExerciseCountList } from '@/statics/js/api'

const { RangePicker } = DatePicker

const GPSExportModal = React.forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState([moment().subtract(30, 'days'), moment()])
  const [firstSelectedDate, setFirstSelectedDate] = useState(null)

  // 暴露open方法给父组件
  React.useImperativeHandle(ref, () => ({
    open: () => {
      setDateRange([moment().subtract(30, 'days'), moment()])
      setFirstSelectedDate(null)
      setVisible(true)
    }
  }))

  // 日期限制函数
  const disabledDate = useCallback(
    (current) => {
      // 不能选择未来日期
      if (current && current > moment().endOf('day')) {
        return true
      }

      // 如果已选择第一个日期，限制第二个日期的范围
      if (firstSelectedDate && current) {
        const diff = Math.abs(current.diff(firstSelectedDate, 'days'))
        return diff > 30 // 限制最多31天
      }

      return false
    },
    [firstSelectedDate]
  )

  // 日历面板变化时的处理函数（选择日期时触发）
  const handleCalendarChange = useCallback((dates) => {
    if (dates && dates.length === 1) {
      // 当选择了第一个日期时，记录下来
      setFirstSelectedDate(dates[0])
    } else if (!dates || dates.length === 0) {
      // 当清空选择时，重置状态
      setFirstSelectedDate(null)
    }
  }, [])

  // 日期选择器打开/关闭时的处理
  const handleOpenChange = useCallback((open) => {
    if (open) {
      // 当打开选择器时，重置第一个选择的日期
      setFirstSelectedDate(null)
    }
  }, [])

  // 处理日期变化
  const handleDateChange = useCallback((dates) => {
    // 更新日期范围状态
    setDateRange(dates)

    // 当清空选择时，重置第一个选择的日期
    if (!dates || dates.length === 0) {
      setFirstSelectedDate(null)
    }
  }, [])

  // 导出函数
  const handleExport = useCallback(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error('请选择有效的时间范围')
      return
    }

    setLoading(true)

    const params = {
      startTime: dateRange[0].format('YYYY-MM-DD'),
      endTime: dateRange[1].format('YYYY-MM-DD')
    }

    _exportBikeExerciseCountList(params)
      .then((res) => {
        if (res.data.code === 200) {
          window.open(res.data.data, '_blank')
          message.success('导出成功')
          setVisible(false)
        } else {
          message.error(res.data.message || '导出失败')
        }
      })
      .catch((err) => {
        console.error(err)
        message.error('导出失败')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [dateRange])

  // 使用防抖包装导出函数
  const debouncedExport = useCallback(
    debounce(() => handleExport(), 500),
    [handleExport]
  )

  return (
    <Modal
      title='导出GPS运动数据'
      visible={visible}
      onOk={debouncedExport}
      onCancel={() => setVisible(false)}
      confirmLoading={loading}
      destroyOnClose
      maskClosable={false}
    >
      <Spin spinning={loading} tip='导出中，请稍候...'>
        <Form layout='vertical' style={{ marginTop: '20px' }}>
          <Form.Item label='选择时间范围' style={{ marginBottom: 0 }}>
            <RangePicker
              value={dateRange}
              onChange={handleDateChange}
              onCalendarChange={handleCalendarChange}
              onOpenChange={handleOpenChange}
              format='YYYY-MM-DD'
              disabledDate={disabledDate}
              allowClear={false}
              style={{ width: '100%' }}
              placeholder={['开始日期', '结束日期']}
              renderExtraFooter={() => <div>日期范围不能超过31天</div>}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  )
})

export default GPSExportModal
